import {
  JsonFormControl,
  makeCommentsControl,
  makeDestinationSelectControl,
  makeNumberControl,
  makeOrientationSelectControl,
  makePassFailControl,
  makeSelectControl,
  makeSerialNumberSelect,
  makeSerialNumberText,
  makeStumpSelectControl,
} from "@components";

/**
 * This file contains all Form Control objects for all **2S** Module production
 * steps. Form conotrol objects added here should be exported to the `index`
 * file, and added as a switch condition in `getFormControl`.
 */

const SENSOR_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("2S.Sensor", "Sensor Serial Number", "sensorSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Images",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const VTRX_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("2S.VTRX", "VTRX Serial", "vtrxSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const SENSOR_KAPTON_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.2S KaptonGluing",
    "Kapton Gluing Fixture",
    "kaptonGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601_LV Glue", "ep601LvGlue"),
  makeStumpSelectControl("Number of Stumps", "stumps", 2),
  makeCommentsControl(),
];

const SENSOR_HV_TAIL_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.2S HVTailGluing",
    "HV Tail Gluing Fixture",
    "hvTailGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601_LV Glue", "ep601LvGlue"),
  makeSelectControl(
    "Sensor Orientation",
    "orientation",
    [
      { value: "top", label: "Top (2 tails)" },
      { value: "bottom", label: "Bottom (1 tail)" },
    ],
    "Please select a sensor orientation",
  ),
  makeCommentsControl(),
];

const SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.2S HVTailWirebond",
    "HV Tail Wirebond Fixture",
    "hvTailWirebondFixture",
  ),
  {
    label: "Wirebond Program ID",
    inputType: "text",
    fieldName: "wirebondProgramId",
    defaultValue: "",
    config: {
      required: "Program ID is required.",
    },
  },
  makeSerialNumberSelect(
    "Aux.Encapsulant.Slyguard_186",
    "Slyguard 186 Encapsulant",
    "slyguard186Encapsulant",
  ),
  makeCommentsControl(),
];

const SENSOR_IV_MEASURMENT: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Sensor", "Sensor Serial Number", "sensorSerial"),
  // TODO: File input
  makePassFailControl("IV Measurement Result"),
  makeSelectControl(
    "HV Measurement Result",
    "hvmeasurement",
    [
      { value: "true", label: "Passed" },
      { value: "false", label: "Failed" },
      { value: "NA", label: "N/A" },
    ],
    "Please select an option",
  ),
  {
    label: "Image",
    inputType: "image",
    fieldName: "image",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const GLUE_SANDWICH: JsonFormControl[] = [
  makeSerialNumberText("2S.Module", "Sandwich Serial Number", "sandwichSerial"),
  makeStumpSelectControl("Number of Stumps", "stumps", 2),
  makeSelectControl(
    "Module Thickness",
    "thickness",
    ["1.8mm", "4.0mm"],
    "Please select a module thickness.",
  ),
  makeSerialNumberSelect(
    "2S.Sensor",
    "Top Sensor Serial Number",
    "topSensorSerial",
  ),
  makeSerialNumberSelect(
    "2S.Sensor",
    "Bottom Sensor Serial Number",
    "bottomSensorSerial",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.SensorGluing",
    "Sensor Gluing Fixture ",
    "sensorGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.TC_437", "TC_437 Gluing ", "tc437Glue"),
  makeSerialNumberSelect(
    "Aux.Fixture.2S ModuleCarrier",
    "Module Carrier Serial Number",
    "moduleCarrier",
  ),
  makeCommentsControl(),
];

const SANDWICH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect(
    "2S.Module",
    "Sandwich Serial Number",
    "sandwichSerial",
  ),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const SANDWICH_METROLOGY_TEST: JsonFormControl[] = [
  // Barcode
  makeSerialNumberSelect(
    "2S.Module",
    "Sandwich Serial Number",
    "sandwichSerial",
  ),
  // 2S sensor metrology fixture barcode
  makeSerialNumberSelect(
    "Aux.Fixture.Metrology",
    "Metrology Fixture",
    "metrologyFixture",
  ),
  // Metrology Station Number
  makeSelectControl(
    "Metrology Station Number",
    "metrologyStation",
    ["1", "2", "3"],
    undefined,
  ),
  // Rotation angle (urad)
  {
    label: "Rotation Angle (urad)",
    inputType: "number",
    fieldName: "rotationAngle",
    defaultValue: "",
    config: {
      required: "Enter a rotation angle in urad.",
    },
  },
  // Sensor parallel offset (um)
  {
    label: "Sensor Parallel Offset (um)",
    fieldName: "sensorParallelOffset",
    inputType: "number",
    defaultValue: "",
    config: {
      required: "Enter a sensor parallel offset in um.",
    },
  },
  // Sensor perpendicular offset (um)
  {
    label: "Sensor Perpendicular Offset (um)",
    fieldName: "sensorPerpendicularOffset",
    inputType: "number",
    defaultValue: "",
    config: {
      required: "Enter a sensor perpendicular offset in um.",
    },
  },
  makeSelectControl(
    "Top Sensor Orientation",
    "topOrientation",
    [
      { value: "normal", label: "Normal" },
      { value: "rotated", label: "Rotated" },
    ],
    "Please select a sensor orientation.",
  ),
  makeSelectControl(
    "Bottom Sensor Orientation",
    "bottomOrientation",
    [
      { value: "normal", label: "Normal" },
      { value: "rotated", label: "Rotated" },
    ],
    "Please select a sensor orientation.",
  ),
  {
    label: "Top Sensor Scratch Pad (autofilled)",
    inputType: "text",
    fieldName: "scratchpadTop",
    defaultValue: "Module not selected or no scratchpad info for this module",
    dependency: "sandwichSerial",
  },
  {
    label: "Top Sensor Scratchpad Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  {
    label: "Bottom Sensor Scratch Pad (autofilled)",
    inputType: "text",
    fieldName: "scratchpadBottom",
    defaultValue: "Module not selected or no scratchpad info for this module",
    dependency: "sandwichSerial",
  },
  {
    label: "Bottom Sensor Scratchpad Image",
    inputType: "multipleimage",
    fieldName: "multipleimage2",
    defaultValue: undefined,
  },
  // Comment (optional)
  makeCommentsControl(),
];

const SEH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("2S.SEH", "SEH Serial Number", "sehSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const FEH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("2S.FEH", "FEH Serial Number", "fehSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const SKELETON_ASSEMBLY: JsonFormControl[] = [
  makeSerialNumberSelect("2S.SEH", "SEH Serial Number", "sehSerial"),
  makeSerialNumberSelect("2S.FEH", "FEH Left Serial Number", "leftFEHSerial"),
  makeSerialNumberSelect("2S.FEH", "FEH Right Serial Number", "rightFEHSerial"),
  makeSelectControl(
    "Module Thickness",
    "thickness",
    ["1.8mm", "4.0mm"],
    "Please select a module thickness.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.SkeletonAssembly",
    "Skeleton Assembly Fixture",
    "skeletonFixture",
  ),
  makeCommentsControl(),
];

const SKELETON_TEST: JsonFormControl[] = [
  makeSerialNumberSelect("2S.SEH", "SEH Serial Number", "sehSerial"),
  // TODO: Submit Result File
  makePassFailControl("Skeleton Test Result"),
  makeCommentsControl(),
];

const GLUE_HYBRID: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  {
    label: "Incomplete Module (No SEH)?",
    options: [
      { label: "Incomplete", value: "yes" },
      { label: "Complete", value: "no" },
    ],
    inputType: "select",
    defaultValue: "Complete",
    fieldName: "isIncomplete",
  },
  makeSerialNumberSelect(
    "2S.SEH",
    "SEH Serial Number",
    "sehSerial",
    undefined,
    [
      {
        depFieldName: "isIncomplete",
        depFieldValue: "yes",
        depFieldValueCondition: "=",
      },
    ],
  ),
  makeSelectControl(
    "Module Thickness",
    "thickness",
    ["1.8mm", "4.0mm"],
    "Please select a module thickness.",
  ),
  makeSelectControl(
    "Points of Contact",
    "contactPoints",
    ["5", "6"],
    "Please select a number of contact points.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.2S HybridGluing",
    "Hybrid Gluing Fixture",
    "hybridGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.TC_437", "TC 437 Glue", "tc437Glue"),
  makeCommentsControl(),
];

const AUX_2S_EXTRA_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect(
    "2S.Module",
    "Sandwich Serial Number",
    "moduleSerial",
    "Please enter a valid serial number.",
    undefined,
    true,
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.SensorGluing",
    "Sensor Gluing Fixture ",
    "sensorGluingFixture",
  ),
  makeSerialNumberSelect("2S.SEH", "SEH Serial Number", "sehSerial"),
  makeCommentsControl(),
];

const MODULE_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const PACK_AND_SHIP_MODULE: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeDestinationSelectControl("Shipping Destination", "destination"),
  makeCommentsControl(),
];

const RETRIEVE_AND_INSPECT_MODULE: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Post Ship Inspection"),
  makeCommentsControl(),
];

const ASSEMBLE_FIRST_SENSOR_WIREBOND: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSelectControl(
    "Top or bottom sensor?",
    "whichSensor",
    [
      { value: "top", label: "Top" },
      { value: "bottom", label: "Bottom" },
    ],
    "Please select a sensor.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.2S ModuleWirebond",
    "Wirebond Fixture",
    "wirebondFixture",
  ),
  {
    label: "Wirebond Program ID",
    inputType: "text",
    fieldName: "wirebondProgramId",
    defaultValue: "",
    config: {
      required: "Please input a Program ID",
    },
  },
  makeCommentsControl(),
];

const ASSEMBLE_SECOND_SENSOR_WIREBOND: JsonFormControl[] = [
  ...ASSEMBLE_FIRST_SENSOR_WIREBOND,
];

const ASSEMBLE_TOP_WIREBOND: JsonFormControl[] = [
  ...ASSEMBLE_FIRST_SENSOR_WIREBOND,
];

const ASSEMBLE_BOTTOM_WIREBOND: JsonFormControl[] = [
  ...ASSEMBLE_FIRST_SENSOR_WIREBOND,
];

const ATTACH_VTRX: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  {
    label: "Is VTRX attached?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    inputType: "select",
    defaultValue: "No",
    fieldName: "isVTRXAttached",
  },
  makeSerialNumberSelect(
    "2S.VTRX",
    "VTRx Serial Number",
    "vtrxSerial",
    undefined,
    [
      {
        depFieldName: "isVTRXAttached",
        depFieldValue: "yes",
        depFieldValueCondition: "=",
      },
    ],
  ),
  makeCommentsControl(),
];

const PRE_ENCAPSULATION_TEST: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeOrientationSelectControl(
    "KIT Box Orientation",
    "kitBox",
    "Plase select a KIT Box Orientation",
  ),
  // TODO: Submit Result File
  makePassFailControl("Pre Encapsulation Test"),
  makeSelectControl(
    "Any bonding issues",
    "bondingissues",
    ["Yes", "No"],
    "Please select a value",
  ),
  makeNumberControl(
    "Number of top left side defective bonds",
    "topLeftDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Top left side defective bond list",
    inputType: "text",
    fieldName: "topLeftDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of top right right defective bonds",
    "topRightDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Top right side defective bond list",
    inputType: "text",
    fieldName: "topRightDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of bottom left side defective bonds",
    "bottomLeftDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Bottom left side defective bond list",
    inputType: "text",
    fieldName: "bottomLeftDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of bottom right side defective bonds",
    "bottomRightDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Bottom right side defective bond list",
    inputType: "text",
    fieldName: "bottomRightDefectString",
    defaultValue: "",
  },
  makeCommentsControl(),
];

const FIRST_SENSOR_ENCAPSULATION: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSelectControl(
    "Top or bottom sensor?",
    "whichSensor",
    [
      { value: "top", label: "Top" },
      { value: "bottom", label: "Bottom" },
    ],
    "Please select a sensor.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.Encapsulation",
    "Encapsulation Fixture",
    "encapsulationFixture",
  ),
  makeSerialNumberSelect(
    "Aux.Encapsulant.Slyguard_186",
    "Slyguard 186",
    "slyguard186",
  ),
  makeCommentsControl(),
];

const SECOND_SENSOR_ENCAPSULATION: JsonFormControl[] = [
  ...FIRST_SENSOR_ENCAPSULATION,
];

const TOP_ENCAPSULATION: JsonFormControl[] = [...FIRST_SENSOR_ENCAPSULATION];

const BOTTOM_ENCAPSULATION: JsonFormControl[] = [...FIRST_SENSOR_ENCAPSULATION];

const FINAL_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Module Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeSelectControl(
    "KIT box orientation",
    "kitboxorientation",
    ["A", "B"],
    "Please select an orientation",
  ),
  makePassFailControl("Pass/fail grade", "passfailgrade"),
  makeSelectControl(
    "Potato grade",
    "potatograde",
    ["A", "B", "C"],
    "Please select a grade",
  ),
  makeCommentsControl(),
];

const BURN_IN_TEST: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeOrientationSelectControl(
    "Burn In Box Side",
    "burnInBoxSide",
    "Please select a Burn In Box side.",
  ),
  makeSelectControl(
    "Burn In Box Slot",
    "burnInBoxSlot",
    ["1", "2", "3", "4", "5"],
    "Please select a Burn In Box Slot",
  ),
  // TODO: File input.
  makePassFailControl("Burn In Test Result"),
  makeCommentsControl(),
];

const SHIP_TO_INTEGRATION: JsonFormControl[] = [
  makeSerialNumberSelect("2S.Module", "Sandwich Serial Number", "moduleSerial"),
  makeDestinationSelectControl("Shipping Destination", "destination", true),
  makeCommentsControl(),
];

const Steps2S: { [key: string]: JsonFormControl[] } = {
  SENSOR_VISUAL_INSPECTION,
  SENSOR_KAPTON_GLUING,
  SENSOR_HV_TAIL_GLUING,
  SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL,
  SENSOR_IV_MEASURMENT,
  GLUE_SANDWICH,
  VTRX_VISUAL_INSPECTION,
  SANDWICH_VISUAL_INSPECTION,
  SANDWICH_METROLOGY_TEST,
  SEH_VISUAL_INSPECTION,
  FEH_VISUAL_INSPECTION,
  SKELETON_ASSEMBLY,
  SKELETON_TEST,
  GLUE_HYBRID,
  MODULE_VISUAL_INSPECTION,
  PACK_AND_SHIP_MODULE,
  RETRIEVE_AND_INSPECT_MODULE,
  ASSEMBLE_FIRST_SENSOR_WIREBOND,
  ASSEMBLE_SECOND_SENSOR_WIREBOND,
  ASSEMBLE_TOP_WIREBOND,
  ASSEMBLE_BOTTOM_WIREBOND,
  ATTACH_VTRX,
  PRE_ENCAPSULATION_TEST,
  FIRST_SENSOR_ENCAPSULATION,
  SECOND_SENSOR_ENCAPSULATION,
  TOP_ENCAPSULATION,
  BOTTOM_ENCAPSULATION,
  FINAL_VISUAL_INSPECTION,
  BURN_IN_TEST,
  SHIP_TO_INTEGRATION,
};

const Steps2SAux: { [key: string]: JsonFormControl[] } = {
  AUX_2S_EXTRA_GLUING,
};

export { Steps2S, Steps2SAux };
